import { initialStateCombinationMaterial } from '../edit-florder-detail/florder-detail.model';
import {
  CombinationMaterial,
  isCombinationMaterial,
  isPackingMaterial,
  isPalletMaterial,
  OrderDetailTO,
  OrderMaterial,
  PackingMaterial,
  Quantity,
} from '../edit-florder-detail/florder-detail.interface';
import * as actions from './ccr-detail.actions';
import { fixQuantitiesForPackings, util } from '../../util/util';
import { initialStateEditOrderDetail } from '../order-detail/order-detail.model';
import { EditOrderDetail } from '../order-detail/order-detail.interface';
import { CCREditMaterialLine } from './ccr-detail.interface';
import { Definitions } from '../definitions/definitions.interface';

let internalId = 0;

function mapMaterial(lineItem, quantity: Quantity, generateInternalIds = false) {
  const type = (lineItem.palletId && lineItem.packingId && 'combination') || (lineItem.palletId && 'pallet') || 'packing';
  // Due to a bug in SAP, quantity for packings is wrong. (see jsDoc fixQuantitiesForPackings)
  const fixedQuantity = (type === 'packing' ? fixQuantitiesForPackings(quantity) : quantity);
  return {
    type,
    internalId: generateInternalIds ? ++internalId : lineItem.itemNumber,
    palletId: lineItem.palletId,
    packingId: lineItem.packingId,
    packingStatus: lineItem.packingStatus,
    logisticsVarietyPacking: lineItem.logisticsVarietyPacking,
    packingsPerPallet: lineItem.packingsPerPallet,
    numberOfPallets: fixedQuantity.numberOfPallets,
    packingQuantity: fixedQuantity.packingQuantity,
    difference: type === 'packing' ? fixQuantitiesForPackings(lineItem.difference) : lineItem.difference,
    loaded: type === 'packing' ? fixQuantitiesForPackings(lineItem.loaded) : lineItem.loaded,
    unloaded: type === 'packing' ? fixQuantitiesForPackings(lineItem.unloaded) : lineItem.unloaded,
  };
}

function prepareMaterial(material: OrderMaterial) {
  if (isCombinationMaterial(material)) {
    material.packingQuantity = util.toNumber(material.numberOfPallets) * util.toNumber(material.packingsPerPallet);
  }
  if (isPackingMaterial(material)) {
    material['numberOfPallets'] = null;
    material['palletId'] = null;
  }

  if (isPalletMaterial(material)) {
    material['packingQuantity'] = null;
    material['packingId'] = null;
    // material.packingsPerPallet = null;
    material.logisticsVarietyPacking = null;
    material.packingStatus = null;
    material.contentId = null;
    material.lineReferenceId = null;
  }
}

function addOrReplaceMaterial(state: EditOrderDetail, material: CCREditMaterialLine, returnState: EditOrderDetail) {
  const existingMaterial = material.internalId && state.orderDetail.ccr.ccrLineItems.find(m => m.internalId === material.internalId);

  if (!material.internalId) {
    // tslint:disable-next-line:no-param-reassign
    material = {
      ...material,
      internalId: ++internalId
    };
  }

  if (existingMaterial) {
    return {
      ...returnState,
      orderDetail: {
        ...returnState.orderDetail,
        ccr: {
          ...returnState.orderDetail.ccr,
          ccrLineItems: state.orderDetail.ccr.ccrLineItems.map(lineItem => {
            const packingMaterial: PackingMaterial = isPackingMaterial(material) ? material : null;
            const combinationMaterial: CombinationMaterial = isCombinationMaterial(material) ? material : null;
            if (lineItem.internalId === material.internalId) {
              const newPackingQuantity = (packingMaterial || combinationMaterial).packingQuantity ? util.toNumber((packingMaterial || combinationMaterial).packingQuantity) : null;
              const newNumberOfPallets = material.numberOfPallets ? util.toNumber(material.numberOfPallets) : null;
              const newPackingsPerPallet = material.packingsPerPallet ? util.toNumber(material.packingsPerPallet) : null;
              return {
                ...lineItem,
                changed: existingMaterial.isNew
                  ? null
                  : {
                      packingQuantity: lineItem['packingQuantity'] !== newPackingQuantity ? newPackingQuantity : null,
                      numberOfPallets: lineItem.numberOfPallets !== newNumberOfPallets ? newNumberOfPallets : null,
                      packingsPerPallet: lineItem.packingsPerPallet !== newPackingsPerPallet ? newPackingsPerPallet : null,
                    },
                packingQuantity: existingMaterial.isNew ? newPackingQuantity : lineItem['packingQuantity'],
                numberOfPallets: existingMaterial.isNew ? newNumberOfPallets : lineItem.numberOfPallets,
                packingsPerPallet: existingMaterial.isNew ? newPackingsPerPallet : lineItem.packingsPerPallet,
              };
            }
            return lineItem;
          }),
        },
      },
    };
  } else {
    return {
      ...returnState,
      orderDetail: {
        ...returnState.orderDetail,
        ccr: {
          ...returnState.orderDetail.ccr,
          ccrLineItems: [...state.orderDetail.ccr.ccrLineItems, material],
        },
      },
    };
  }
}

function reduceOpenFlorder(state: EditOrderDetail, dataTO: OrderDetailTO, definitions: Definitions) {
  return Object.assign({}, state, {
    mode: Object.assign({}, state.mode, {
      loading: false,
      loadSuccess: true,
    }),
    orderDetail: {
      customer: dataTO.soldTo,
      deliveryMethod: {
        soldTo: dataTO.soldTo && dataTO.soldTo.id,
        to: dataTO.to,
        from: dataTO.from,
        shippingCondition: dataTO.transport && definitions.transport.type.find(t => t.id === dataTO.transport),
        customerReferenceNumber: dataTO.customerRefNumber,
        senderRefNumber: dataTO.senderRefNumber,
        transporter: dataTO.transporter,
        licensePlate: dataTO.licensePlate,
        remarks: dataTO.comments,
      },
      planning: {
        recurrence: null,
        loadingDate: dataTO.confirmedLoadingDate || dataTO.confirmedUnloadingDate || dataTO.requestedUnloadingDate || dataTO.requestedLoadingDate,
        requestedUnloadingDate: dataTO.requestedUnloadingDate,
        confirmedUnloadingDate: dataTO.confirmedUnloadingDate,
        exchangePallets: dataTO.exchangePallets || false,
        numberOfExchangePallets: dataTO.numberOfExchangePallets || false,
      },
      requestedBalance: '',
      etmOrderNumber: dataTO.etmOrderNumber,
      registeredBy: dataTO.registeredBy,
      salesOrderNumber: dataTO.salesOrderNumber,
      deliveryNumber: dataTO.deliveryNumber,
      shipmentNumbers: dataTO.shipmentNumber,
    },
  });
}

export function reducer(state = util.deepCopy(initialStateEditOrderDetail), action: actions.Actions): EditOrderDetail {
  switch (action.type) {
    case actions.CCRDETAIL_OPEN: {
      return Object.assign({}, state, {
        mode: {
          type: 'ccr',
          state: 'create',
          editWindow: 'detail',
          loading: true,
        },
      });
    }

    case actions.CCRDETAIL_OPEN_SUCCESS: {
      const material = util.deepCopy(initialStateCombinationMaterial) as OrderMaterial;
      const dataTO: OrderDetailTO = action.payload.orderDetail;
      const returnState = reduceOpenFlorder(state, dataTO, action.payload.definitions);

      // map lineItems
      let ccrLineItems;
      if (action.payload.createOrEdit === 'create') {
        ccrLineItems = dataTO.lineItems.map(li => mapMaterial(li, li));
      } else {
        ccrLineItems = dataTO.ccr.ccrLineItems
          .filter(li => !!li.loaded) // RFC1703: Mail Piet 24/01/22: Ik stel voor – als voorlopige oplossing - om alleen de regels met loaded aantallen te tonen in het scherm voor wijzigen CCR. De materialen die niet in de oorspronkelijke CCR zaten maar wel in de C&I zijn dan niet zichtbaar.
          .map(li => mapMaterial(li, li.loaded, true));
      }

      return Object.assign({}, returnState, {
        mode: {
          ...returnState.mode,
          state: action.payload.createOrEdit, // create or update
          material: state.mode.state === 'create' ? material : null,
        },
        orderDetail: {
          ...returnState.orderDetail,
          status: dataTO.orderStatus,
          deliveryMethod: {
            ...returnState.orderDetail.deliveryMethod,
            type: dataTO.orderType,
          },
          planning: {
            ...returnState.orderDetail.planning,
            orderDate: dataTO.orderDate,
          },
          materials: [],
          ccr: {
            ccrNumber: dataTO.ccr.ccrNumber,
            sealNumber: dataTO.ccr.sealNumber,
            ccrLineItems,
          },
        },
      });
    }

    case actions.CCRDETAIL_OPEN_FAIL: {
      return Object.assign({}, state, {
        mode: Object.assign({}, state.mode, {
          loading: false,
          loadSuccess: false,
        }),
      });
    }

    case actions.CCRDETAIL_CREATE_SAVE_MATERIAL: {
      const material = util.deepCopy(action.payload);
      // for packing, numberOfPallets (sometimes?) filled in with value of packingQuantity
      if (!(material.palletId?.trim()) && material.packingId?.trim() && material.numberOfPallets) {
        material.numberOfPallets = material.packingQuantity;
      }
      const newMaterial = util.deepCopy(initialStateCombinationMaterial) as OrderMaterial;
      const returnState = Object.assign({}, state, {
        mode: {
          type: 'ccr',
          state: state.mode.state,
          editWindow: state.mode.editWindow,
          material: newMaterial,
          saving: false,
        },
      });
      prepareMaterial(material);
      return addOrReplaceMaterial(state, material, returnState);
    }

    case actions.CCRDETAIL_EDIT_SUBMIT:
    case actions.CCRDETAIL_CREATE_SUBMIT: {
      return Object.assign({}, state, {
        mode: { ...state.mode, saving: true, saveSuccess: false, saved: false },
      });
    }

    case actions.CCRDETAIL_EDIT_SUBMIT_SUCCESS:
    case actions.CCRDETAIL_CREATE_SUBMIT_SUCCESS: {
      return Object.assign({}, state, {
        mode: {
          ...state.mode,
          saving: false,
          saveSuccess: true,
          saved: true,
        },
        orderDetail: Object.assign({}, state.orderDetail, {
          ccr: Object.assign({}, state.orderDetail.ccr, {
            ccrNumber: action.payload.ccrNumber,
          }),
        }),
      });
    }

    case actions.CCRDETAIL_EDIT_SUBMIT_FAIL:
    case actions.CCRDETAIL_CREATE_SUBMIT_FAIL: {
      return Object.assign({}, state, {
        mode: {
          ...state.mode,
          saving: false,
          saveSuccess: false,
          saved: true,
        },
        orderDetail: Object.assign({}, state.orderDetail, {
          ccr: Object.assign({}, state.orderDetail.ccr, {
            ccrNumber: '',
          }),
        }),
      });
    }

    case actions.CCRDETAIL_CREATE_START_EDIT_MATERIAL: {
      const material = action.payload;
      return {
        ...state,
        mode: {
          type: 'ccr',
          state: state.mode.state,
          editWindow: state.mode.editWindow,
          material: {
            ...material,
            packingsPerPallet: material.changed?.packingsPerPallet || material.packingsPerPallet,
            packingQuantity: material.changed?.packingQuantity || material['packingQuantity'],
            numberOfPallets: material.changed?.numberOfPallets || material.numberOfPallets,
          },
        },
      };
    }

    case actions.CCRDETAIL_CREATE_DELETE_MATERIAL: {
      return Object.assign({}, state, {
        orderDetail: Object.assign({}, state.orderDetail, {
          ccr: Object.assign({}, state.orderDetail.ccr, {
            ccrLineItems: state.orderDetail.ccr.ccrLineItems.filter(m => m !== action.payload),
          }),
        }),
      });
    }

    default: {
      return state;
    }
  }
}
